import { useState } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/Input';

/**
 * Lista de palabras escrita como FICHITAS, no como texto separado por comas.
 *
 * El campo de antes era un Input con "alias, otro alias" adentro: para borrar
 * una palabra había que encontrar su coma, y una coma de más dejaba un alias
 * vacío que se descartaba en silencio al guardar. Acá se escribe y se aprieta
 * Enter; cada palabra queda como una fichita con su ✕.
 */
export function EntradaDeFichas({
  label,
  valores,
  onAgregar,
  onQuitar,
  placeholder,
  ayuda,
  guia,
}: {
  label: string;
  valores: string[];
  onAgregar: (texto: string) => void;
  onQuitar: (texto: string) => void;
  placeholder?: string;
  ayuda?: string;
  /** Ancla de "Muéstrame", si esta entrada es parte de una guía. */
  guia?: string;
}) {
  const [texto, setTexto] = useState('');

  function agregar() {
    onAgregar(texto);
    setTexto('');
  }

  return (
    <div>
      <Input
        data-guia={guia}
        label={label}
        value={texto}
        placeholder={placeholder}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          // Enter agrega; la coma también, porque es como venía escribiéndose.
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            agregar();
          }
        }}
        // Lo escrito y no confirmado se agrega igual al salir del campo: nadie
        // debería perder una palabra por no haber apretado Enter.
        onBlur={agregar}
      />
      {valores.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {valores.map((v) => (
            <li
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              {v}
              <button
                type="button"
                aria-label={`Quitar ${v}`}
                onClick={() => onQuitar(v)}
                className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-xs text-slate-400">
        {ayuda ?? 'Escribe y aprieta Enter para agregar cada una.'}
      </p>
    </div>
  );
}
